import { useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Alert, Box, Button, CircularProgress, Collapse, Divider, Stack, Typography } from '@mui/material';

import type { SubjectLanguage } from '../types';
import type { DisplayState } from '../vocabulary';

import { type DeliveryHistoryEntry,DeliveryHistoryList } from './DeliveryHistoryList';
import { RequirementStateChip } from './RequirementStateChip';
import { WaiveRequirementDialog } from './WaiveRequirementDialog';

/**
 * One row, with its display state already derived by the container. The
 * per-patient DTO now carries its own `label` and `kind`, so naming a row no
 * longer needs a join against the org catalog.
 */
export interface RequirementRowView {
  patientRequirementId: string;
  label: string;
  state: DisplayState;
  /** "Requested on Aug 30", "Waived — patient pays cash". */
  detail?: string;
  /**
   * The kind has a step a patient can complete online (GDN-3607).
   *
   * Not a display flag: it decides whether the PATIENT-level send has anything
   * to reach. Rows carry no send of their own — see the props below.
   */
  hasPatientStep?: boolean;
  /** Open or chased, as opposed to settled. */
  isOutstanding?: boolean;
  /** A live request exists on this row (AC-4's Send/Remind distinction). */
  hasActiveSession?: boolean;
  /** AC-8: only an outstanding row can be waived. */
  canWaive?: boolean;
  /** AC-8: only a waived row can be reopened. */
  canReopen?: boolean;
}

export interface PatientRequirementsSectionViewProps {
  rows: RequirementRowView[];
  /** Rows still owed — the count in the header (AC-3). */
  missingCount: number;
  isLoading: boolean;
  isError: boolean;
  /** Delivery history for the whole patient (AC-20), shared by the rows. */
  deliveryHistory: DeliveryHistoryEntry[];
  /** The history failed to load — never rendered as "nothing has been sent". */
  isHistoryError?: boolean;
  onRetryHistory?: () => void;
  /**
   * Requirement ids with a write in flight. Only those rows' controls disable —
   * a set rather than one id so overlapping actions do not release each other.
   */
  busyRequirementIds?: ReadonlySet<string>;
  subject: SubjectLanguage;
  /** Patient-level, no requirement id — the endpoint has no slot for one. */
  onSend: () => void;
  /** At least one open row has an online step, so a send would reach it. */
  canSend?: boolean;
  /** "Send request" or "Remind", decided by the rows the send would reach. */
  sendLabel?: string;
  isSending?: boolean;
  /**
   * The server's own words when it declined the send — a 409, most often
   * `no_online_step`. Shown on the control, because a conflict is the server's
   * settled answer rather than a fault to retry (GDN-3558).
   */
  sendDeclined?: string;
  onWaive: (patientRequirementId: string, reason: string) => void;
  onReopen: (patientRequirementId: string) => void;
  onRetry: () => void;
}

/**
 * The Requirements section on the patient record (GDN-3226; AC-3, AC-4, AC-8,
 * AC-20).
 *
 * Waive and reopen are offered per row rather than per section, and only where
 * they can actually act: `canWaive` on outstanding rows, `canReopen` on waived
 * ones. Withholding the control — not just the handler — is deliberate, so
 * there is never a button that looks live and does nothing.
 */
export const PatientRequirementsSectionView = ({
  rows,
  missingCount,
  isLoading,
  isError,
  deliveryHistory,
  isHistoryError,
  onRetryHistory,
  busyRequirementIds,
  subject,
  onSend,
  canSend = false,
  sendLabel = 'Send request',
  isSending = false,
  sendDeclined,
  onWaive,
  onReopen,
  onRetry,
}: PatientRequirementsSectionViewProps) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [waiving, setWaiving] = useState<RequirementRowView | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        We could not load requirements for this {subject.singular}.
      </Alert>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
        This practice has no requirements set up.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: ELIG_TOKENS.ink }}>
          {missingCount === 0
            ? 'Nothing outstanding'
            : `${missingCount} item${missingCount === 1 ? '' : 's'} still needed`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/*
          One send, for the patient (GDN-3608). The endpoint is patient-scoped
          and takes no requirement id, so this is the shape that tells the
          truth: it reaches the patient's open items that have an online step,
          and it says so once rather than once per row.

          Withheld, not disabled, when nothing qualifies — the server would
          answer 409 `no_online_step`, which is worth rendering when it happens
          but not worth inviting.
        */}
        {canSend && (
          <Button
            size="small"
            variant="outlined"
            disabled={isSending}
            onClick={onSend}
            startIcon={isSending ? <CircularProgress size={12} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
          >
            {sendLabel}
          </Button>
        )}
        {/* Also shown when the history FAILED: gating the toggle on a
            non-empty list made a failed load indistinguishable from no
            deliveries, because both left the list empty. */}
        {(deliveryHistory.length > 0 || isHistoryError) && (
          <Button
            size="small"
            variant="text"
            onClick={() => setHistoryOpen(open => !open)}
            sx={{ textTransform: 'none', fontSize: '0.75rem', color: ELIG_TOKENS.inkSoft }}
          >
            {historyOpen ? 'Hide delivery history' : 'Delivery history'}
          </Button>
        )}
      </Stack>

      {/*
        The server's answer, in its own words, under the control that asked for
        it. Amber rather than red: a 409 is a settled answer about this patient,
        not a fault, so it must not read as "try again" — retrying cannot change
        it (the same rule as the recorder alert, GDN-3558).
      */}
      {sendDeclined && (
        <Typography
          role="status"
          sx={{ fontSize: 12, fontWeight: 600, color: ELIG_TOKENS.amber, pb: 0.5 }}
        >
          {sendDeclined}
        </Typography>
      )}

      <Collapse in={historyOpen} unmountOnExit>
        <Box sx={{ my: 1 }}>
          {/* A failed REFRESH must not discard records we already hold. The
              full alert is only for the case where there is nothing to show;
              with entries in hand the notice sits above them and they stay
              readable, secure links included. */}
          {isHistoryError && deliveryHistory.length === 0 ? (
            <Alert
              severity="warning"
              action={
                onRetryHistory && (
                  <Button color="inherit" size="small" onClick={onRetryHistory}>
                    Retry
                  </Button>
                )
              }
            >
              We could not load what has been sent. This does not mean nothing
              was sent.
            </Alert>
          ) : (
            <>
              {isHistoryError && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1 }}
                  action={
                    onRetryHistory && (
                      <Button color="inherit" size="small" onClick={onRetryHistory}>
                        Retry
                      </Button>
                    )
                  }
                >
                  Could not refresh. Showing what we last loaded, which may be
                  out of date.
                </Alert>
              )}
              <DeliveryHistoryList entries={deliveryHistory} />
            </>
          )}
        </Box>
      </Collapse>

      <Divider />

      {rows.map(row => {
        const settled = row.state === 'received' || row.state === 'waived' || row.state === 'cancelled';
        const busy = busyRequirementIds?.has(row.patientRequirementId) ?? false;
        return (
          <Box key={row.patientRequirementId} sx={{ py: 1.25, opacity: settled ? 0.75 : 1 }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
            >
              <Typography
                sx={{ fontSize: 14.5, fontWeight: 650, color: ELIG_TOKENS.ink, minWidth: 180 }}
              >
                {row.label}
              </Typography>
              <RequirementStateChip state={row.state} />
              <Box sx={{ flex: 1 }} />
              {/*
                No Send here (GDN-3607, GDN-3608). The send route is
                patient-scoped and takes no requirement id, so a per-row button
                promised something it could not do: pressing it on a step-less
                row recorded a Reminder against a different requirement. The
                one control that matches the call sits in the header above.

                What stays are the actions that genuinely target this row —
                Waive and Reopen both pass its requirement id.
              */}
              {row.canWaive && (
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => setWaiving(row)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', color: ELIG_TOKENS.inkSoft }}
                >
                  Waive
                </Button>
              )}
              {row.canReopen && (
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => onReopen(row.patientRequirementId)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', color: ELIG_TOKENS.inkSoft }}
                >
                  Reopen
                </Button>
              )}
            </Stack>
            {row.detail && (
              <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft, lineHeight: 1.4 }}>
                {row.detail}
              </Typography>
            )}
          </Box>
        );
      })}

      <WaiveRequirementDialog
        row={waiving}
        subject={subject}
        onClose={() => setWaiving(null)}
        onConfirm={reason => {
          if (waiving) onWaive(waiving.patientRequirementId, reason);
          setWaiving(null);
        }}
      />
    </Stack>
  );
};
